import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExternalLink, Sparkles } from "lucide-react";
import { formatJamaicaDate } from "@/lib/datetime";
import { formatCount, formatRate, getTrials, metricsUrls, monthLabel, type TrialsResponse } from "@/lib/metrics";
import { EmptyState, ErrorState, TableSkeleton, Tile } from "./shared";

export default function TrialsTab({ onOpenCustomer }: { onOpenCustomer: (customerId: string) => void }) {
  const [months, setMonths] = useState(12);
  const { data, isLoading, error } = useQuery<TrialsResponse>({
    queryKey: [metricsUrls.trials({ months })],
    queryFn: () => getTrials(months),
  });

  if (isLoading) return <Card><CardContent className="pt-6"><TableSkeleton rows={8} cols={6} /></CardContent></Card>;
  if (error) return <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>;
  if (!data) return null;

  const { totals, cohorts, active } = data;
  const endingSoon = active.filter((a) => a.daysLeft <= 3).length;

  return (
    <div className="space-y-6" data-testid="tab-trials">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Trials started" value={formatCount(totals.started)} sub={`Last ${months} months`} testId="tile-trials-started" />
        <Tile label="Converted to paid" value={formatCount(totals.converted)} tone={totals.converted > 0 ? "good" : undefined} />
        <Tile label="Conversion rate" value={formatRate(totals.rate)} sub="Of trials that have finished" />
        <Tile
          label="Trialing right now"
          value={formatCount(active.length)}
          sub={endingSoon ? `${endingSoon} end within 3 days` : "None ending in the next 3 days"}
          tone={endingSoon > 0 ? "warn" : undefined}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" />Trial cohorts</CardTitle>
              <CardDescription>Of everyone who started a trial in a month, how many went on to pay and how long they took to decide.</CardDescription>
            </div>
            <Tabs value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <TabsList className="h-9">
                <TabsTrigger value="6">6 months</TabsTrigger>
                <TabsTrigger value="12">12 months</TabsTrigger>
                <TabsTrigger value="24">24 months</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {cohorts.length === 0 ? (
            <EmptyState title="No trials in this window" hint="Nobody has started a trial in the last few months, or trials are not switched on for any plan." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Started</TableHead>
                    <TableHead className="text-right">Converted</TableHead>
                    <TableHead className="text-right">Still trialing</TableHead>
                    <TableHead className="text-right">Conversion rate</TableHead>
                    <TableHead className="text-right">Avg days to convert</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cohorts.map((c) => (
                    <TableRow key={c.month} data-testid={`trial-cohort-${c.month}`}>
                      <TableCell className="whitespace-nowrap font-medium">{monthLabel(c.month)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCount(c.started)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{formatCount(c.converted)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCount(c.stillTrialing)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatRate(c.rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.avgDaysToConvert == null ? "–" : c.avgDaysToConvert.toFixed(1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Trialing right now</CardTitle>
          <CardDescription>Anyone with three days or less is worth a nudge before the card is charged.</CardDescription>
        </CardHeader>
        <CardContent>
          {active.length === 0 ? (
            <EmptyState title="Nobody is on a trial" hint="When someone starts a trial they will appear here with the days left on the clock." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Trial ends</TableHead>
                    <TableHead className="text-right">Days left</TableHead>
                    <TableHead>Last active</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.map((a) => (
                    <TableRow
                      key={a.subscriptionId}
                      className={a.daysLeft <= 3 ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}
                      data-testid={`active-trial-${a.subscriptionId}`}
                    >
                      <TableCell className="max-w-[16rem]">
                        <button
                          type="button"
                          className="font-medium text-primary hover:underline text-left truncate block"
                          onClick={() => onOpenCustomer(a.customerId)}
                        >
                          {a.name || a.email || a.customerId}
                        </button>
                        <div className="text-xs text-muted-foreground truncate">{a.email || "–"}</div>
                      </TableCell>
                      <TableCell>{a.grade != null ? <Badge variant="outline" className="text-[10px] py-0 h-4">G{a.grade}</Badge> : "–"}</TableCell>
                      <TableCell className="capitalize text-sm">{a.plan || "–"}{a.interval ? <span className="text-xs text-muted-foreground"> / {a.interval}</span> : null}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{formatJamaicaDate(a.trialEnd)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <Badge className={`border-0 ${a.daysLeft <= 3 ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-muted text-muted-foreground"}`}>
                          {a.daysLeft}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{a.lastActiveAt ? formatJamaicaDate(a.lastActiveAt) : "Never"}</TableCell>
                      <TableCell className="text-right">
                        {a.userId ? (
                          <Link href={`/admin/users?openUser=${a.userId}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                            Open user <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
